/**
 * A stable id for this browser.
 *
 * It is the tie-break in `pickNewer`, so it has to survive reloads: if it were
 * regenerated each session, two edits made in the same instant could resolve
 * differently on each device and never converge.
 *
 * localStorage rather than IndexedDB deliberately — the value is needed
 * synchronously, including inside the lazy schema backfill, and an async read
 * there would mean a plan could be written before its own device id existed.
 */

const KEY = 'eden-eats-device-id';

let cached: string | null = null;

function makeId(): string {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID();
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function deviceId(): string {
  if (cached) return cached;
  try {
    const stored = globalThis.localStorage?.getItem(KEY);
    if (stored) {
      cached = stored;
      return stored;
    }
  } catch {
    // Private browsing, or storage blocked. Fall through to a session-only id:
    // merging still works, it just cannot tie-break consistently across
    // reloads on a device that cannot remember anything anyway.
  }
  const made = makeId();
  cached = made;
  try {
    globalThis.localStorage?.setItem(KEY, made);
  } catch {
    // As above — non-fatal.
  }
  return made;
}

/** Test-only: forget the cached id so a test can simulate a second device. */
export function resetDeviceIdForTests(): void {
  cached = null;
}
